import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { UsersThree } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { IAuditoriaEmpresa, IPessoal, IPessoalEmpregado } from '../types';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';
import { SquadHeader } from './pessoal/SquadHeader';
import { EquipeGallery } from './pessoal/EquipeGallery';
import { PeopleAnalytics } from './pessoal/PeopleAnalytics';
import { FolhaPanel } from './pessoal/FolhaPanel';
import { CollabDrawer } from './pessoal/CollabDrawer';
import { IsoOffice } from './iso/IsoOffice';
import { peopleFromEmpregados } from './iso/officePeople';

interface PessoalScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
}

type Aba = 'equipe' | 'folha' | 'pessoas' | 'movimentacoes' | 'guias';

// Filtro de situação do quadro. 'ativos' = não-demitidos (Ativo + Afastado), pra a tela
// não inflar com ex-funcionários (que costumam superar os ativos). Afastado é quem tem
// afastamento vigente hoje (vem do backend em `situacao`).
type StatusFiltro = 'ativos' | 'afastados' | 'demitidos' | 'todos';

const TABS: { value: Aba; label: string }[] = [
  { value: 'equipe', label: 'Equipe' },
  { value: 'folha', label: 'Folha' },
  { value: 'pessoas', label: 'Pessoas' },
  { value: 'movimentacoes', label: 'Movimentações' },
  { value: 'guias', label: 'Guias' },
];

export function PessoalScreen({ empresa, periodo }: PessoalScreenProps) {
  const { getPessoal } = useAuditoriaInteligente();
  const [aba, setAba] = useState<Aba>('equipe');
  const [sel, setSel] = useState<IPessoalEmpregado | null>(null);
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>('ativos');

  const { data, isFetching } = useQuery<IPessoal>({
    queryKey: ['ai-pessoal', { id: empresa?.id, ...periodo }],
    queryFn: getPessoal,
    enabled: !!empresa,
  });

  const f = data?.resumo_folha;

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver o departamento pessoal."
        icon={UsersThree}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Box textAlign="center" py={16}>
        <Spinner />
      </Box>
    );
  }

  const empregados = data?.empregados ?? [];
  const afastamentos = data?.afastamentos ?? [];
  const rescisoes = data?.rescisoes ?? [];

  // Situação derivada (backend): Ativo / Afastado / Demitido. O quadro inflava com
  // demitidos (que costumam superar os ativos) — default mostra só os não-demitidos.
  const counts = { ativos: 0, afastados: 0, demitidos: 0, todos: empregados.length };
  empregados.forEach(e => {
    if (e.situacao === 'Demitido') counts.demitidos += 1;
    else {
      counts.ativos += 1; // não-demitido (inclui afastado)
      if (e.situacao === 'Afastado') counts.afastados += 1;
    }
  });
  const empregadosView = ((): IPessoalEmpregado[] => {
    if (statusFiltro === 'afastados') return empregados.filter(e => e.situacao === 'Afastado');
    if (statusFiltro === 'demitidos') return empregados.filter(e => e.situacao === 'Demitido');
    if (statusFiltro === 'todos') return empregados;
    return empregados.filter(e => e.situacao !== 'Demitido'); // ativos (não-demitidos)
  })();

  const FILTROS: { value: StatusFiltro; label: string; n: number }[] = [
    { value: 'ativos', label: 'Ativos', n: counts.ativos },
    { value: 'afastados', label: 'Afastados', n: counts.afastados },
    { value: 'demitidos', label: 'Demitidos', n: counts.demitidos },
    { value: 'todos', label: 'Todos', n: counts.todos },
  ];

  return (
    <Box>
      <SquadHeader
        empregados={empregadosView}
        folha={f}
        rescisoes={rescisoes.length}
        serie={data?.headcount_turnover}
      />

      {/* Filtro de situação do quadro */}
      <Flex gap={2} mb={5} mt={1} wrap="wrap" align="center">
        <Text fontSize="11px" fontWeight={700} color={UI.muted} textTransform="uppercase" mr={1}>
          Situação
        </Text>
        {FILTROS.map(filtro => {
          const on = filtro.value === statusFiltro;
          return (
            <Flex
              key={filtro.value}
              as="button"
              onClick={() => setStatusFiltro(filtro.value)}
              align="center"
              gap={1.5}
              px={3}
              py={1.5}
              borderRadius="full"
              border="1px solid"
              borderColor={on ? UI.brand : UI.border}
              bg={on ? UI.brand : 'transparent'}
              color={on ? 'white' : UI.muted}
              _hover={{ borderColor: UI.brand }}
              transition="all .12s"
            >
              <Text fontSize="12px" fontWeight={700}>
                {filtro.label}
              </Text>
              <Box
                fontSize="11px"
                fontWeight={800}
                px={1.5}
                borderRadius="full"
                bg={on ? 'rgba(255,255,255,0.25)' : UI.rowHover}
              >
                {filtro.n}
              </Box>
            </Flex>
          );
        })}
      </Flex>

      {/* Abas */}
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {TABS.map(t => {
          const isActive = t.value === aba;
          return (
            <Flex
              key={t.value}
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setAba(t.value)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {t.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {aba === 'equipe' && (
        <Box>
          {empregadosView.length > 0 && (
            <Box mb={5}>
              <IsoOffice people={peopleFromEmpregados(empregadosView)} seed={empresa?.id ?? 1} />
            </Box>
          )}
          <EquipeGallery empregados={empregadosView} onSelect={setSel} />
        </Box>
      )}

      {aba === 'folha' && (
        <FolhaPanel folha={f} qtdColaboradores={f?.qtd_empregados ?? counts.ativos} />
      )}

      {aba === 'pessoas' && (
        <PeopleAnalytics empregados={empregadosView} serie={data?.headcount_turnover} />
      )}

      {aba === 'movimentacoes' && (
        <Box>
          <Text
            fontSize="12px"
            fontWeight={800}
            color={UI.muted}
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={3}
          >
            Rescisões ({rescisoes.length})
          </Text>
          <Box
            bg="white"
            border="1px solid"
            borderColor={UI.border}
            borderRadius="16px"
            overflow="hidden"
            mb={6}
          >
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Colaborador</Th>
                    <Th>Cargo</Th>
                    <Th>Admissão</Th>
                    <Th>Demissão</Th>
                    <Th>Motivo</Th>
                    <Th isNumeric>Dias</Th>
                    <Th isNumeric>Verbas</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rescisoes.map(r => (
                    <Tr key={`${r.nome}-${r.demissao}`} _hover={{ bg: UI.rowHover }}>
                      <Td fontWeight={700}>
                        {r.nome}
                        {r.ma_contratacao && (
                          <Badge
                            ml={2}
                            style={{ backgroundColor: UI.negSoft, color: UI.neg }}
                            borderRadius="full"
                            px={2}
                            fontSize="10px"
                            fontWeight={700}
                            textTransform="none"
                          >
                            &lt; 90 dias
                          </Badge>
                        )}
                      </Td>
                      <Td>{r.cargo}</Td>
                      <Td>{r.admissao}</Td>
                      <Td>{r.demissao}</Td>
                      <Td maxW="200px" whiteSpace="normal">
                        {r.motivo}
                      </Td>
                      <Td isNumeric color={r.dias <= 90 ? UI.neg : UI.ink} fontWeight={700}>
                        {r.dias}
                      </Td>
                      <Td isNumeric>{formatCurrency(r.proventos)}</Td>
                    </Tr>
                  ))}
                  {rescisoes.length === 0 && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8} color={UI.muted}>
                        Nenhuma rescisão no período.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          <Text
            fontSize="12px"
            fontWeight={800}
            color={UI.muted}
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={3}
          >
            Afastamentos ({afastamentos.length})
          </Text>
          <Box
            bg="white"
            border="1px solid"
            borderColor={UI.border}
            borderRadius="16px"
            overflow="hidden"
          >
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Colaborador</Th>
                    <Th>Cargo</Th>
                    <Th>Tipo</Th>
                    <Th>Início</Th>
                    <Th>Fim</Th>
                    <Th isNumeric>Dias</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {afastamentos.map(a => (
                    <Tr key={`${a.nome}-${a.data_inicio}-${a.tipo}`} _hover={{ bg: UI.rowHover }}>
                      <Td fontWeight={700}>{a.nome}</Td>
                      <Td>{a.cargo}</Td>
                      <Td>
                        <Badge
                          style={{ backgroundColor: UI.infoSoft, color: UI.brand }}
                          borderRadius="full"
                          px={2.5}
                          fontSize="11px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {a.tipo}
                        </Badge>
                      </Td>
                      <Td>{a.data_inicio}</Td>
                      <Td color={a.data_fim ? UI.ink : UI.muted}>{a.data_fim ?? 'em curso'}</Td>
                      <Td isNumeric fontWeight={700}>
                        {a.dias}
                      </Td>
                    </Tr>
                  ))}
                  {afastamentos.length === 0 && (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={8} color={UI.muted}>
                        Nenhum afastamento no período.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {aba === 'guias' && (
        <Box
          bg="white"
          border="1px solid"
          borderColor={UI.border}
          borderRadius="16px"
          overflow="hidden"
        >
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Guia</Th>
                  <Th>Competência</Th>
                  <Th>Descrição</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.guias ?? []).map(g => (
                  <Tr
                    key={`${g.guia}-${g.competencia}-${g.descricao}`}
                    _hover={{ bg: UI.rowHover }}
                  >
                    <Td fontWeight={700}>{g.guia}</Td>
                    <Td>{g.competencia}</Td>
                    <Td>{g.descricao}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(g.valor)}
                    </Td>
                  </Tr>
                ))}
                {(data?.guias ?? []).length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={8} color={UI.muted}>
                      Sem guias no período.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {data?.indisponiveis && data.indisponiveis.length > 0 && (
        <Text fontSize="11.5px" color={UI.muted} mt={4}>
          Blocos sem fonte nesta base: {data.indisponiveis.join(', ')}.
        </Text>
      )}

      <CollabDrawer
        emp={sel}
        rescisoes={rescisoes}
        afastamentos={afastamentos}
        isOpen={!!sel}
        onClose={() => setSel(null)}
      />
    </Box>
  );
}
